<script lang="ts">
	import { page } from '$app/stores';
	import { locale, _ } from 'svelte-i18n';
	import { onMount } from 'svelte';
	import { theme } from '$lib/stores/theme';
	import { docSections } from '$lib/docs/content';
	import { availableDocSlugs } from '$lib/docs/loader';
	import type { DocSection, DocItem, Language } from '$lib/docs/types';

	// Drop nav entries that point to docs without a markdown file, so the
	// sidebar never links to a 404. A parent whose own page is missing but
	// that has live children is kept and pointed at its first live child.
	function pruneSections(list: DocSection[]): DocSection[] {
		return list
			.map((section) => ({
				...section,
				items: section.items
					.map((item): DocItem | null => {
						const children = (item.children || []).filter((c) => availableDocSlugs.has(c.slug));
						const selfOk = availableDocSlugs.has(item.slug);
						if (!selfOk && children.length === 0) return null;
						return {
							...item,
							slug: selfOk ? item.slug : children[0].slug,
							children: children.length ? children : undefined
						};
					})
					.filter((item): item is DocItem => item !== null)
			}))
			.filter((section) => section.items.length > 0);
	}
	import DocSearch from '$lib/components/docs/DocSearch.svelte';
	import OSSelector from '$lib/components/docs/OSSelector.svelte';

	interface Props {
		children?: any;
		mode: 'docs' | 'guides';
	}

	let { children, mode }: Props = $props();

	// State
	let sidebarOpen = $state(false);
	let sections = $state<DocSection[]>([]);
	let expandedItems = $state<Set<string>>(new Set());
	let expandedSections = $state<Set<string>>(new Set());

	// Reactive computations
	const currentLocale = $derived(($locale as Language) || 'en');
	const currentPath = $derived($page.url.pathname);
	const isDarkMode = $derived($theme === 'dark');
	const activeTab = $derived(currentPath.startsWith('/guides') ? 'guides' : 'docs');

	// Generate breadcrumbs
	const breadcrumbs = $derived(() => {
		const parts = currentPath.split('/').filter(Boolean);
		const crumbs = [];

		if (parts.length > 0 && (parts[0] === 'docs' || parts[0] === 'guides')) {
			if (parts.length > 1) {
				const currentSlug = parts[1];

				// For guides, just show the guide name
				if (parts[0] === 'guides') {
					const guideNames: Record<string, string> = {
						'freelancer-setup': 'Setting Up as a Freelancer',
						'team-setup': 'Setting Up Your Team',
						'first-invoice': 'Creating Your First Invoice',
						'project-workflow': 'Project Management Workflow',
						'time-tracking-workflow': 'Time Tracking Best Practices',
						'client-collaboration': 'Client Collaboration Guide',
						'financial-workflow': 'Financial Management',
						'calendar-setup': 'Connecting Your Calendar',
						'banking-setup': 'Bank Account Integration',
						'import-export': 'Import & Export Data',
						'keyboard-shortcuts': 'Keyboard Shortcuts',
						automation: 'Automation Tips',
						'productivity-tips': 'Productivity Hacks'
					};

					if (guideNames[currentSlug]) {
						crumbs.push({ label: 'Guides', href: '/guides', isSection: false });
						crumbs.push({ label: guideNames[currentSlug], href: `/guides/${currentSlug}` });
					}
				} else {
					// Find the current page in sections
					for (const section of sections) {
						for (const item of section.items) {
							if (item.slug === currentSlug) {
								// Add section (non-navigable)
								crumbs.push({ label: section.title, href: null, isSection: true });
								// Add page
								crumbs.push({ label: item.title, href: `/docs/${item.slug}` });
								return crumbs;
							}
							// Check children
							if (item.children) {
								for (const child of item.children) {
									if (child.slug === currentSlug) {
										// Add section (non-navigable)
										crumbs.push({ label: section.title, href: null, isSection: true });
										// Add parent page
										crumbs.push({ label: item.title, href: `/docs/${item.slug}` });
										// Add child page
										crumbs.push({ label: child.title, href: `/docs/${child.slug}` });
										return crumbs;
									}
								}
							}
						}
					}
				}
			}
		}

		return crumbs;
	});

	// Update sections when locale changes
	$effect(() => {
		sections = pruneSections(docSections[currentLocale] || docSections['en'] || []);
	});

	// Handle keyboard shortcuts
	function handleKeydown(event: KeyboardEvent) {
		// Escape to close sidebar
		if (event.key === 'Escape' && sidebarOpen) {
			sidebarOpen = false;
		}
	}

	// Close sidebar when clicking outside on mobile
	function handleOverlayClick() {
		sidebarOpen = false;
	}

	// Toggle sidebar
	function toggleSidebar() {
		sidebarOpen = !sidebarOpen;
	}

	// Toggle expanded state for items
	function toggleExpanded(itemId: string) {
		if (expandedItems.has(itemId)) {
			expandedItems.delete(itemId);
		} else {
			expandedItems.add(itemId);
		}
		expandedItems = new Set(expandedItems);
	}

	// Toggle expanded state for sections
	function toggleSection(sectionId: string) {
		const section = sections.find((s) => s.id === sectionId);

		if (expandedSections.has(sectionId)) {
			expandedSections.delete(sectionId);
			// Track that this section was manually collapsed if it contains active item
			if (section && isSectionActive(section)) {
				manuallyToggled.add(sectionId);
			}
		} else {
			expandedSections.add(sectionId);
			// Remove from manually toggled since user is expanding it
			manuallyToggled.delete(sectionId);
		}
		expandedSections = new Set(expandedSections);
	}

	// Check if item or its children are active
	function isItemOrChildActive(item: DocItem): boolean {
		if (currentPath === `/docs/${item.slug}`) return true;
		if (item.children) {
			return item.children.some((child) => currentPath === `/docs/${child.slug}`);
		}
		return false;
	}

	// Check if section contains active item
	function isSectionActive(section: DocSection): boolean {
		return section.items.some((item) => {
			if (currentPath === `/docs/${item.slug}`) return true;
			if (item.children) {
				return item.children.some((child) => currentPath === `/docs/${child.slug}`);
			}
			return false;
		});
	}

	// Track which sections were manually toggled
	let manuallyToggled = new Set<string>();

	// Initialize expanded sections on first load or when sections change
	let initialized = false;
	let lastPath = '';

	$effect(() => {
		// Only auto-expand on initial load or when sections structure changes
		if (!initialized && sections.length > 0) {
			sections.forEach((section) => {
				// Auto-expand section if it contains the active item
				if (isSectionActive(section)) {
					expandedSections.add(section.id);
				}

				section.items.forEach((item) => {
					if (item.children && isItemOrChildActive(item)) {
						expandedItems.add(item.id);
					}
				});
			});
			expandedSections = new Set(expandedSections);
			expandedItems = new Set(expandedItems);
			initialized = true;
			lastPath = currentPath;
		}
	});

	// When path changes, only expand the active section if it wasn't manually collapsed
	$effect(() => {
		if (initialized && currentPath && currentPath !== lastPath) {
			sections.forEach((section) => {
				// If section contains active item, isn't expanded, and wasn't manually collapsed
				if (
					isSectionActive(section) &&
					!expandedSections.has(section.id) &&
					!manuallyToggled.has(section.id)
				) {
					expandedSections.add(section.id);
					expandedSections = new Set(expandedSections);
				}

				section.items.forEach((item) => {
					if (item.children && isItemOrChildActive(item) && !expandedItems.has(item.id)) {
						expandedItems.add(item.id);
						expandedItems = new Set(expandedItems);
					}
				});
			});

			// Clear manual toggle tracking for sections that no longer contain active items
			manuallyToggled.forEach((sectionId) => {
				const section = sections.find((s) => s.id === sectionId);
				if (section && !isSectionActive(section)) {
					manuallyToggled.delete(sectionId);
				}
			});

			lastPath = currentPath;
		}
	});

	onMount(() => {
		// Close sidebar on route changes (mobile)
		const unsubscribe = page.subscribe(() => {
			if (window.innerWidth < 1024) {
				sidebarOpen = false;
			}
		});

		return unsubscribe;
	});
</script>

<svelte:window onkeydown={handleKeydown} />

<!-- Documentation Top Bar -->
<div class="docs-topbar">
	<div class="docs-topbar__container">
		<!-- Search (left side) -->
		<div class="docs-topbar__search">
			<DocSearch locale={currentLocale} />
		</div>

		<!-- Breadcrumbs (middle) -->
		<nav class="docs-breadcrumbs">
			<!-- Home icon (navigates to current section home) -->
			<a
				href={activeTab === 'guides' ? '/guides' : '/docs'}
				class="docs-breadcrumbs__home"
				aria-label={$_('docs.navigation.breadcrumbs.homeAriaLabel', {
					values: {
						section:
							activeTab === 'guides'
								? $_('docs.navigation.tabs.guides')
								: $_('docs.navigation.tabs.documentation')
					}
				})}
			>
				<svg
					width="16"
					height="16"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
					stroke-linejoin="round"
				>
					<path
						d="M4 19v-7a2 2 0 0 1 .6-1.43l7-6.45a2 2 0 0 1 2.8 0l7 6.45A2 2 0 0 1 22 12v7a2 2 0 0 1-2 2h-5a1 1 0 0 1-1-1v-4a1 1 0 0 0-1-1h-2a1 1 0 0 0-1 1v4a1 1 0 0 1-1 1H4a2 2 0 0 1-2-2Z"
					/>
				</svg>
			</a>

			{#each breadcrumbs() as crumb, i}
				<span class="docs-breadcrumbs__separator">
					<svg
						width="16"
						height="16"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						stroke-width="2"
						stroke-linecap="round"
						stroke-linejoin="round"
					>
						<polyline points="9 18 15 12 9 6"></polyline>
					</svg>
				</span>

				{#if crumb.isSection}
					<!-- Section (non-navigable) -->
					<span class="docs-breadcrumbs__section">{crumb.label}</span>
				{:else if i === breadcrumbs().length - 1}
					<!-- Current page -->
					<span class="docs-breadcrumbs__current">{crumb.label}</span>
				{:else}
					<!-- Navigable page -->
					<a href={crumb.href} class="docs-breadcrumbs__link">{crumb.label}</a>
				{/if}
			{/each}
		</nav>

		<!-- OS Selector (right side) -->
		<div class="docs-topbar__os-selector">
			<OSSelector />
		</div>
	</div>
</div>

<!-- Mobile overlay -->
{#if sidebarOpen}
	<button
		class="docs-overlay"
		onclick={handleOverlayClick}
		onkeydown={(e) => e.key === 'Escape' && handleOverlayClick()}
		aria-label="Close sidebar"
		tabindex="0"
	></button>
{/if}

<div class="docs-container" class:docs-container--dark={isDarkMode}>
	<!-- Sidebar -->
	<aside class="docs-sidebar" class:docs-sidebar--open={sidebarOpen}>
		<!-- Tabs for switching between Documentation and Guides -->
		<div class="docs-sidebar__tabs">
			<a
				href="/docs"
				class="docs-sidebar__tab"
				class:docs-sidebar__tab--active={activeTab === 'docs'}
			>
				{$_('docs.navigation.tabs.documentation')}
			</a>
			<a
				href="/guides"
				class="docs-sidebar__tab"
				class:docs-sidebar__tab--active={activeTab === 'guides'}
			>
				{$_('docs.navigation.tabs.guides')}
			</a>
		</div>

		<div class="docs-sidebar__content">
			{#if activeTab === 'docs'}
				<!-- Documentation Navigation -->
				<nav class="docs-nav">
					{#each sections as section}
						<div class="docs-nav__section">
							<button
								class="docs-nav__section-title"
								class:docs-nav__section-title--expanded={expandedSections.has(section.id)}
								class:docs-nav__section-title--active={isSectionActive(section)}
								onclick={() => toggleSection(section.id)}
							>
								<span>{section.title.toUpperCase()}</span>
								<svg
									class="docs-nav__section-arrow"
									class:docs-nav__section-arrow--expanded={expandedSections.has(section.id)}
									width="12"
									height="12"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								>
									<polyline points="9 18 15 12 9 6" />
								</svg>
							</button>
							{#if expandedSections.has(section.id)}
								<ul class="docs-nav__items">
									{#each section.items as item}
										<li class="docs-nav__item">
											{#if item.children && item.children.length > 0}
												<div class="docs-nav__expandable">
													<button
														class="docs-nav__link docs-nav__link--parent"
														class:docs-nav__link--expanded={expandedItems.has(item.id)}
														class:docs-nav__link--active-parent={isItemOrChildActive(item)}
														onclick={() => toggleExpanded(item.id)}
													>
														<span class="docs-nav__text">{item.title}</span>
														<svg
															class="docs-nav__arrow"
															class:docs-nav__arrow--expanded={expandedItems.has(item.id)}
															width="16"
															height="16"
															viewBox="0 0 24 24"
															fill="none"
															stroke="currentColor"
															stroke-width="2"
															stroke-linecap="round"
															stroke-linejoin="round"
														>
															<polyline points="9 18 15 12 9 6" />
														</svg>
													</button>
													{#if expandedItems.has(item.id)}
														<ul class="docs-nav__children">
															{#each item.children as child}
																<li class="docs-nav__child-item">
																	<a
																		href="/docs/{child.slug}"
																		class="docs-nav__link docs-nav__link--child"
																		class:docs-nav__link--active={currentPath ===
																			`/docs/${child.slug}`}
																	>
																		{child.title}
																	</a>
																</li>
															{/each}
														</ul>
													{/if}
												</div>
											{:else}
												<a
													href="/docs/{item.slug}"
													class="docs-nav__link"
													class:docs-nav__link--active={currentPath === `/docs/${item.slug}`}
												>
													{item.title}
												</a>
											{/if}
										</li>
									{/each}
								</ul>
							{/if}
						</div>
					{/each}
				</nav>
			{:else}
				<!-- Guides Navigation -->
				<nav class="docs-nav">
					<div class="docs-nav__section">
						<h3 class="docs-nav__section-title docs-nav__section-title--expanded">
							<span>{$_('docs.navigation.sections.quickStart')}</span>
						</h3>
						<ul class="docs-nav__items">
							<li class="docs-nav__item">
								<a
									href="/guides/freelancer-setup"
									class="docs-nav__link"
									class:docs-nav__link--active={currentPath === '/guides/freelancer-setup'}
								>
									Setting Up as a Freelancer
								</a>
							</li>
							<li class="docs-nav__item">
								<a
									href="/guides/team-setup"
									class="docs-nav__link"
									class:docs-nav__link--active={currentPath === '/guides/team-setup'}
								>
									Setting Up Your Team
								</a>
							</li>
							<li class="docs-nav__item">
								<a
									href="/guides/first-invoice"
									class="docs-nav__link"
									class:docs-nav__link--active={currentPath === '/guides/first-invoice'}
								>
									Creating Your First Invoice
								</a>
							</li>
						</ul>
					</div>

					<div class="docs-nav__section">
						<h3 class="docs-nav__section-title docs-nav__section-title--expanded">
							<span>{$_('docs.navigation.sections.workflows')}</span>
						</h3>
						<ul class="docs-nav__items">
							<li class="docs-nav__item">
								<a
									href="/guides/project-workflow"
									class="docs-nav__link"
									class:docs-nav__link--active={currentPath === '/guides/project-workflow'}
								>
									Project Management Workflow
								</a>
							</li>
							<li class="docs-nav__item">
								<a
									href="/guides/time-tracking-workflow"
									class="docs-nav__link"
									class:docs-nav__link--active={currentPath === '/guides/time-tracking-workflow'}
								>
									Time Tracking Best Practices
								</a>
							</li>
							<li class="docs-nav__item">
								<a
									href="/guides/client-collaboration"
									class="docs-nav__link"
									class:docs-nav__link--active={currentPath === '/guides/client-collaboration'}
								>
									Client Collaboration Guide
								</a>
							</li>
							<li class="docs-nav__item">
								<a
									href="/guides/financial-workflow"
									class="docs-nav__link"
									class:docs-nav__link--active={currentPath === '/guides/financial-workflow'}
								>
									Financial Management
								</a>
							</li>
						</ul>
					</div>

					<div class="docs-nav__section">
						<h3 class="docs-nav__section-title docs-nav__section-title--expanded">
							<span>{$_('docs.navigation.sections.integrations')}</span>
						</h3>
						<ul class="docs-nav__items">
							<li class="docs-nav__item">
								<a
									href="/guides/calendar-setup"
									class="docs-nav__link"
									class:docs-nav__link--active={currentPath === '/guides/calendar-setup'}
								>
									Connecting Your Calendar
								</a>
							</li>
							<li class="docs-nav__item">
								<a
									href="/guides/banking-setup"
									class="docs-nav__link"
									class:docs-nav__link--active={currentPath === '/guides/banking-setup'}
								>
									Bank Account Integration
								</a>
							</li>
							<li class="docs-nav__item">
								<a
									href="/guides/import-export"
									class="docs-nav__link"
									class:docs-nav__link--active={currentPath === '/guides/import-export'}
								>
									Import & Export Data
								</a>
							</li>
						</ul>
					</div>

					<div class="docs-nav__section">
						<h3 class="docs-nav__section-title docs-nav__section-title--expanded">
							<span>{$_('docs.navigation.sections.tipsTricks')}</span>
						</h3>
						<ul class="docs-nav__items">
							<li class="docs-nav__item">
								<a
									href="/guides/keyboard-shortcuts"
									class="docs-nav__link"
									class:docs-nav__link--active={currentPath === '/guides/keyboard-shortcuts'}
								>
									Keyboard Shortcuts
								</a>
							</li>
							<li class="docs-nav__item">
								<a
									href="/guides/automation"
									class="docs-nav__link"
									class:docs-nav__link--active={currentPath === '/guides/automation'}
								>
									Automation Tips
								</a>
							</li>
							<li class="docs-nav__item">
								<a
									href="/guides/productivity-tips"
									class="docs-nav__link"
									class:docs-nav__link--active={currentPath === '/guides/productivity-tips'}
								>
									Productivity Hacks
								</a>
							</li>
						</ul>
					</div>
				</nav>
			{/if}
		</div>
	</aside>

	<!-- Main Content -->
	<main class="docs-main">
		<!-- Mobile menu toggle -->
		<div class="docs-mobile-header">
			<button class="docs-mobile-toggle" onclick={toggleSidebar} aria-label="Toggle navigation">
				<span class="docs-mobile-toggle__line"></span>
				<span class="docs-mobile-toggle__line"></span>
				<span class="docs-mobile-toggle__line"></span>
			</button>
			<span class="docs-mobile-title"
				>{activeTab === 'guides'
					? $_('docs.navigation.tabs.guides')
					: $_('docs.navigation.tabs.documentation')}</span
			>
		</div>

		<!-- Page Content -->
		<div class="docs-content">
			{@render children?.()}
		</div>
	</main>
</div>

<style>
	/* Documentation Top Bar */
	.docs-topbar {
		position: sticky;
		top: var(--header-height, 52px);
		z-index: 40;
		background: var(--bg-primary);
		border-bottom: 1px solid var(--border-light);
		padding: 0.75rem 0;
	}

	.docs-topbar__container {
		max-width: none;
		margin: 0;
		padding: 0;
		display: flex;
		align-items: center;
		justify-content: flex-start;
		gap: 0;
	}

	/* Breadcrumbs */
	.docs-breadcrumbs {
		display: flex;
		align-items: center;
		gap: 0.25rem;
		font-size: 0.8125rem;
		flex: 1;
		min-width: 0;
		padding: 0 1.5rem;
	}

	.docs-breadcrumbs__home {
		display: flex;
		align-items: center;
		justify-content: center;
		color: var(--text-tertiary);
		text-decoration: none;
		transition: color 0.15s ease;
		padding: 0.25rem;
		border-radius: 4px;
	}

	.docs-breadcrumbs__home:hover {
		color: var(--text-primary);
		background: var(--bg-tertiary);
	}

	.docs-breadcrumbs__link {
		color: var(--text-tertiary);
		text-decoration: none;
		transition: color 0.15s ease;
		white-space: nowrap;
	}

	.docs-breadcrumbs__link:hover {
		color: var(--text-primary);
	}

	.docs-breadcrumbs__section {
		color: var(--text-tertiary);
		font-weight: 500;
		white-space: nowrap;
		text-transform: uppercase;
		font-size: 0.75rem;
		letter-spacing: 0.05em;
		opacity: 0.7;
	}

	.docs-breadcrumbs__current {
		color: var(--text-primary);
		font-weight: 500;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.docs-breadcrumbs__separator {
		display: flex;
		align-items: center;
		color: var(--text-tertiary);
		opacity: 0.5;
	}

	.docs-breadcrumbs__separator svg {
		width: 12px;
		height: 12px;
	}

	/* Search in top bar */
	.docs-topbar__search {
		width: 280px;
		flex-shrink: 0;
		padding: 0 1rem;
		border-right: 1px solid var(--border-light);
		display: flex;
		align-items: center;
	}

	.docs-topbar__search :global(.search-button) {
		width: 100%;
	}

	/* OS Selector in topbar */
	.docs-topbar__os-selector {
		margin-left: auto;
		padding-right: 1.5rem;
	}

	.docs-topbar__os-selector :global(.os-selector) {
		margin: 0;
		padding: 0.5rem 0.75rem;
		background: transparent;
		border: none;
	}

	.docs-topbar__os-selector :global(.os-selector__label) {
		display: none;
	}

	.docs-topbar__os-selector :global(.os-selector__button) {
		padding: 0.25rem 0.5rem;
		font-size: 0.75rem;
	}

	/* Main container adjustments */
	.docs-container {
		display: flex;
		max-width: none;
		margin: 0;
		min-height: calc(100vh - var(--header-height, 52px) - 49px); /* Subtract topbar height */
		position: relative;
		isolation: isolate;
		background: var(--bg-primary);
		color: var(--text-primary);
	}

	/* Sidebar - always reserves space on desktop */
	.docs-sidebar {
		width: 280px;
		background: transparent;
		border-right: 1px solid var(--border-light);
		height: 100vh;
		overflow: hidden;
		display: flex;
		flex-direction: column;
		flex-shrink: 0;
		position: sticky;
		top: 0;
	}

	/* Tabs for switching between Documentation and Guides */
	.docs-sidebar__tabs {
		display: flex;
		padding: 0.75rem 0.875rem;
		gap: 0.25rem;
		background: var(--bg-secondary);
		border-bottom: 1px solid var(--border-light);
	}

	.docs-sidebar__tab {
		flex: 1;
		padding: 0.5rem 0.75rem;
		background: transparent;
		border: none;
		border-radius: 6px;
		font-size: 0.8125rem;
		font-weight: 500;
		color: var(--text-tertiary);
		cursor: pointer;
		transition: all 0.15s ease;
		text-align: center;
		text-decoration: none;
		display: block;
	}

	.docs-sidebar__tab:hover {
		color: var(--text-secondary);
		background: var(--bg-tertiary);
	}

	.docs-sidebar__tab--active {
		color: var(--text-primary);
		background: var(--bg-primary);
		box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
	}

	.docs-sidebar__tab--active:hover {
		color: var(--text-primary);
		background: var(--bg-primary);
	}

	/* Mobile override - use overlay approach */
	@media (max-width: 1023px) {
		.docs-sidebar {
			position: fixed;
			left: 0;
			bottom: 0;
			z-index: var(--z-overlay);
			transform: translateX(-100%);
			transition: transform 0.3s ease;
		}

		.docs-sidebar--open {
			transform: translateX(0);
		}
	}

	.docs-sidebar__content {
		flex: 1;
		overflow-y: auto;
		padding: 1rem 0.875rem;
		scrollbar-width: thin;
		scrollbar-color: var(--border-light) transparent;
	}

	.docs-sidebar__content::-webkit-scrollbar {
		width: 6px;
	}

	.docs-sidebar__content::-webkit-scrollbar-track {
		background: transparent;
	}

	.docs-sidebar__content::-webkit-scrollbar-thumb {
		background: var(--border-default);
		border-radius: 3px;
	}

	/* Navigation */
	.docs-nav__section {
		margin-bottom: 1.25rem;
	}

	.docs-nav__section-title {
		display: flex;
		align-items: center;
		justify-content: space-between;
		width: 100%;
		font-weight: 600;
		color: var(--text-tertiary);
		font-size: 0.75rem;
		letter-spacing: 0.1em;
		margin-bottom: 0.375rem;
		padding: 0.25rem 0.25rem 0.25rem 0;
		background: none;
		border: none;
		cursor: pointer;
		transition: color 0.15s ease;
		text-align: left;
	}

	.docs-nav__section-title:hover {
		color: var(--text-secondary);
	}

	.docs-nav__section-title--active {
		color: var(--text-secondary);
	}

	.docs-nav__section-arrow {
		flex-shrink: 0;
		transition: transform 0.2s ease;
		color: var(--text-tertiary);
		opacity: 0.5;
	}

	.docs-nav__section-arrow--expanded {
		transform: rotate(90deg);
	}

	.docs-nav__items {
		list-style: none;
		padding: 0;
		margin: 0;
		overflow: hidden;
		animation: slideDown 0.2s ease;
	}

	.docs-nav__item {
		margin-bottom: 0.0625rem;
	}

	.docs-nav__link {
		display: block;
		padding: 0.25rem 0.5rem;
		color: var(--text-secondary);
		text-decoration: none;
		border-radius: 4px;
		font-size: 0.8125rem;
		transition: all 0.15s ease;
		position: relative;
		line-height: 1.4;
	}

	.docs-nav__link:hover {
		color: var(--text-primary);
	}

	.docs-nav__link--active {
		color: var(--text-primary);
		font-weight: 500;
	}

	.docs-nav__link--active::before {
		content: '';
		position: absolute;
		left: -1rem;
		top: 50%;
		transform: translateY(-50%);
		width: 3px;
		height: 16px;
		background: var(--brand-primary);
		border-radius: 2px;
	}

	/* Expandable items */
	.docs-nav__expandable {
		position: relative;
	}

	button.docs-nav__link--parent,
	.docs-nav__link.docs-nav__link--parent {
		display: flex;
		align-items: center;
		justify-content: space-between;
		width: 100%;
		text-align: left;
		background: transparent !important;
		border: none !important;
		cursor: pointer;
		font-family: inherit;
		font-size: inherit;
		line-height: inherit;
		padding: 0.25rem 0.5rem;
		color: var(--text-secondary);
		text-decoration: none;
		border-radius: 4px;
		font-size: 0.8125rem;
		transition: all 0.15s ease;
		position: relative;
	}

	button.docs-nav__link--parent:hover,
	.docs-nav__link.docs-nav__link--parent:hover {
		background: transparent !important;
		color: var(--text-primary);
	}

	.docs-nav__link--expanded,
	.docs-nav__link--parent.docs-nav__link--expanded {
		background: transparent !important;
	}

	.docs-nav__text {
		flex: 1;
	}

	.docs-nav__arrow {
		flex-shrink: 0;
		transition: transform 0.2s ease;
		margin-left: auto;
		color: var(--text-tertiary);
	}

	.docs-nav__arrow--expanded {
		transform: rotate(90deg);
	}

	.docs-nav__link--active-parent {
		color: var(--text-primary) !important;
		font-weight: 500;
		background: transparent !important;
	}

	.docs-nav__children {
		list-style: none;
		padding: 0;
		margin: 0.125rem 0 0 0;
		overflow: hidden;
		animation: slideDown 0.2s ease;
	}

	.docs-nav__child-item {
		margin-bottom: 0;
	}

	.docs-nav__link--child {
		padding-left: 1.75rem;
		font-size: 0.75rem;
		color: var(--text-tertiary);
		line-height: 1.4;
	}

	.docs-nav__link--child:hover {
		color: var(--text-secondary);
	}

	.docs-nav__link--child.docs-nav__link--active {
		color: var(--text-primary);
		font-weight: 500;
	}

	@keyframes slideDown {
		from {
			opacity: 0;
			max-height: 0;
		}
		to {
			opacity: 1;
			max-height: 500px;
		}
	}

	/* Main Content */
	.docs-main {
		flex: 1;
		min-width: 0;
		position: relative;
	}

	.docs-mobile-header {
		display: flex;
		align-items: center;
		gap: 1rem;
		padding: 1rem 1.5rem;
		border-bottom: 1px solid var(--border-light);
		background: var(--bg-primary);
		position: sticky;
		top: 0;
		z-index: 50;
	}

	.docs-mobile-toggle {
		display: flex;
		flex-direction: column;
		gap: 4px;
		background: none;
		border: none;
		cursor: pointer;
		padding: 0.5rem;
		border-radius: 4px;
		transition: background-color 0.2s ease;
	}

	.docs-mobile-toggle:hover {
		background: var(--bg-tertiary);
	}

	.docs-mobile-toggle__line {
		width: 20px;
		height: 2px;
		background: var(--text-primary);
		transition: all 0.2s ease;
	}

	.docs-mobile-title {
		font-weight: 600;
		color: var(--text-primary);
	}

	.docs-content {
		padding: 3rem 2rem;
		max-width: 900px;
		margin: 0 auto;
	}

	/* Overlay - only on mobile */
	.docs-overlay {
		position: fixed;
		inset: 0;
		background: var(--bg-overlay);
		z-index: 50;
		display: block;
		border: none;
		cursor: pointer;
		padding: 0;
	}

	/* Desktop Styles */
	@media (min-width: 1024px) {
		.docs-mobile-header {
			display: none;
		}

		.docs-content {
			padding: 4rem 3rem;
		}

		.docs-overlay {
			display: none;
		}
	}

	/* Mobile Improvements */
	@media (max-width: 768px) {
		.docs-sidebar {
			width: 100%;
			max-width: 320px;
		}

		/* Main content uses full width on mobile */
		.docs-main {
			width: 100%;
		}

		/* Hide search on mobile in topbar (it's in mobile menu) */
		.docs-topbar__search {
			display: none;
		}

		/* Hide OS selector on mobile */
		.docs-topbar__os-selector {
			display: none;
		}

		.docs-topbar__container {
			padding: 0 1rem;
		}

		.docs-breadcrumbs {
			font-size: 0.75rem;
		}

		.docs-mobile-header {
			backdrop-filter: blur(10px);
			background: var(--bg-primary) / 0.95;
		}

		.docs-mobile-toggle {
			position: relative;
		}

		.docs-sidebar--open ~ .docs-main .docs-mobile-toggle__line:nth-child(1) {
			transform: rotate(45deg) translate(5px, 5px);
		}

		.docs-sidebar--open ~ .docs-main .docs-mobile-toggle__line:nth-child(2) {
			opacity: 0;
		}

		.docs-sidebar--open ~ .docs-main .docs-mobile-toggle__line:nth-child(3) {
			transform: rotate(-45deg) translate(7px, -6px);
		}

		.docs-content {
			padding: 1.5rem 1rem;
		}

		.docs-nav__link {
			padding: 0.375rem 0.75rem;
			font-size: 0.8125rem;
		}
	}
</style>
